// Trust Centre data — figures updated manually by the operator.
// TripAdvisor renders as an official embed widget so its live count stays on
// the widget side and doesn't need to be duplicated here.

export const trustLastUpdated = "2026-07-22";

export interface ReviewSource {
  platform: "Google" | "TripAdvisor";
  rating: number;
  count: number;
  profileUrl: string;
  updateMode: "manual" | "widget";
}

export const reviewSources: ReviewSource[] = [
  {
    platform: "Google",
    rating: 5.0,
    count: 229,
    profileUrl: "https://share.google/ztMbAZx6wyQ5I38yI",
    updateMode: "manual",
  },
  {
    platform: "TripAdvisor",
    rating: 5.0,
    count: 140,
    profileUrl:
      "https://www.tripadvisor.com/Attraction_Review-g227947-d26258230-Reviews-Vilamoura_Dreams_Cruises-Vilamoura_Quarteira_Faro_District_Algarve.html",
    updateMode: "widget",
  },
];

// Replace with the operator's TripAdvisor widget location ID to enable
// the live rating panel. While it starts with "REPLACE_" the widget is
// suppressed and the static card above is used instead.
export const tripadvisorLocationId = "REPLACE_WITH_TA_LOCATION_ID";

export interface TrustBadge {
  id: string;
  title: { en: string; pt: string };
  body: { en: string; pt: string };
  authority: string;
  authorityUrl: string;
  logoDomain?: string;
}

export const authorityLogo = (domain: string) =>
  `https://www.google.com/s2/favicons?domain=www.${domain}&sz=128`;
export const authorityLogoFallback = (domain: string) =>
  `https://icons.duckduckgo.com/ip3/www.${domain}.ico`;

export const trustBadges: TrustBadge[] = [
  {
    id: "rnaat",
    logoDomain: "turismodeportugal.pt",
    title: { en: "Licensed tour operator", pt: "Operador marítimo-turístico licenciado" },
    body: {
      en: "LUXURY REFERENCE, LDA, trading as VILAMOURA DREAMS (brand registered at INPI), operates under RNAAT licence 743/2022 issued by Turismo de Portugal — the legal condition for maritime-tourism operations in Portugal.",
      pt: "A LUXURY REFERENCE, LDA, que opera sob a marca VILAMOURA DREAMS (marca registada no INPI), atua ao abrigo da licença RNAAT 743/2022 emitida pelo Turismo de Portugal — a condição legal para operações marítimo-turísticas em Portugal.",
    },
    authority: "RNAAT",
    authorityUrl: "https://rnt.turismodeportugal.pt/",
  },
  {
    id: "t360",
    logoDomain: "turismodeportugal.pt",
    title: { en: "Sustainability driven", pt: "Orientados para a sustentabilidade" },
    body: {
      en: "Member of the Turismo 360 programme by Turismo de Portugal — a national framework aligned with the UN Sustainable Development Goals covering environmental, social and economic commitments for tourism operators.",
      pt: "Membro do programa Turismo 360 do Turismo de Portugal — um referencial nacional alinhado com os Objetivos de Desenvolvimento Sustentável da ONU que agrega compromissos ambientais, sociais e económicos para operadores turísticos.",
    },
    authority: "Turismo 360º",
    authorityUrl: "https://business.turismodeportugal.pt/pt/Gerir/reconhecimento-externo/empresas-turismo-360/Paginas/default.aspx",
  },
  {
    id: "gdpr",
    logoDomain: "cnpd.pt",
    title: { en: "Data privacy", pt: "Privacidade de dados" },
    body: {
      en: "All data (name, email, phone, dietary or medical requirements, passenger manifests) is processed under the EU General Data Protection Regulation and UK-GDPR. We align our practices with the USA's FTC guidelines and Canada's PIPEDA.",
      pt: "Todos os dados (nome, email, telefone, restrições alimentares ou médicas, manifestos de passageiros) são tratados ao abrigo do Regulamento Geral de Proteção de Dados da UE e do UK-GDPR. Alinhamos as práticas com as orientações da FTC dos EUA e com o PIPEDA canadiano.",
    },
    authority: "CNPD",
    authorityUrl: "https://www.cnpd.pt",
  },
  {
    id: "crew",
    logoDomain: "dgrm.mm.gov.pt",
    title: { en: "Certified maritime crew", pt: "Tripulação marítima certificada" },
    body: {
      en: "Navigation is operated by permanent captains holding skipper licences issued by DGRM, the national authority for the purpose. All crew members hold valid maritime medical fitness and are covered by the operator's professional insurance.",
      pt: "A navegação é operada por capitães permanentes com licenças de skipper emitidas pela DGRM, a autoridade nacional competente. Toda a tripulação possui cédula médica marítima válida e está coberta pelo seguro profissional do operador.",
    },
    authority: "DGRM",
    authorityUrl: "https://www.dgrm.mm.gov.pt",
  },
  {
    id: "payments",
    logoDomain: "stripe.com",
    title: { en: "Secure payments", pt: "Pagamentos seguros" },
    body: {
      en: "Card payments are processed by Stripe, certified PCI-DSS Level 1 (highest tier). We accept Visa, Mastercard, American Express, Apple Pay, Google Pay, SEPA bank transfers and Multibanco. Card details are never stored on our servers.",
      pt: "Os pagamentos com cartão são processados pela Stripe, certificada PCI-DSS Nível 1 (o mais alto). Aceitamos Visa, Mastercard, American Express, Apple Pay, Google Pay, transferências SEPA e Multibanco. Os dados do cartão não são armazenados nos nossos servidores.",
    },
    authority: "PCI-DSS",
    authorityUrl: "https://www.pcisecuritystandards.org/",
  },
  {
    id: "insurance",
    logoDomain: "asf.com.pt",
    title: { en: "Four layers of insurance", pt: "Quatro camadas de seguros" },
    body: {
      en: "Every charter is insured according to the law in force: guests' personal accidents (death, permanent disability and medical expenses), bodily injury and material damage to third parties, crew accidents and hull & machinery for the yacht.",
      pt: "Todos os cruzeiros estão segurados conforme a lei em vigor: acidentes pessoais dos hóspedes (morte, invalidez permanente e despesas médicas), danos corporais e materiais a terceiros, acidentes da tripulação e cascos & máquinas do iate.",
    },
    authority: "RNAAT",
    authorityUrl: "https://rnt.turismodeportugal.pt/",
  },
  {
    id: "survey",
    logoDomain: "amn.pt",
    title: { en: "Annual legal survey", pt: "Vistoria legal anual" },
    body: {
      en: "The yacht is subject to rigorous annual surveys by the Portuguese Maritime Authority covering seaworthiness, life-saving equipment, firefighting gear, navigation electronics and structural integrity. The operation runs with valid inspection certificates and full safety equipment on board.",
      pt: "O iate é sujeito a vistorias anuais rigorosas pela Autoridade Marítima Nacional que abrangem navegabilidade, meios de salvação, combate a incêndios, eletrónica de navegação e integridade estrutural. A operação decorre com certificados de vistoria válidos e todos os meios de segurança a bordo.",
    },
    authority: "AMN",
    authorityUrl: "https://www.amn.pt",
  },
  {
    id: "consumer",
    title: { en: "Consumer rights", pt: "Direitos do consumidor" },
    body: {
      en: "Clear terms aligned with EU consumer-protection standards. No hidden fees, no small print. Every booking is covered by the Portuguese Electronic Complaints Book, monitored by ASAE (the national regulator), giving guests from the UK, Ireland, USA and Canada the same protection.",
      pt: "Termos claros, alinhados com os padrões europeus de defesa do consumidor. Sem taxas ocultas nem letras miudinhas. Todas as reservas estão cobertas pelo Livro de Reclamações Eletrónico português, fiscalizado pela ASAE (autoridade reguladora), garantindo aos hóspedes do Reino Unido, Irlanda, EUA e Canadá a mesma proteção.",
    },
    authority: "Livro de Reclamações",
    authorityUrl: "https://www.livroreclamacoes.pt/",
  },
];

export interface GuestOrigin {
  code: "gb" | "ie" | "us" | "ca";
  flag: string;
  colors: string[];
  label: { en: string; pt: string };
}

export const guestOrigins: GuestOrigin[] = [
  { code: "gb", flag: "🇬🇧", colors: ["#012169", "#FFFFFF", "#C8102E"], label: { en: "United Kingdom", pt: "Reino Unido" } },
  { code: "ie", flag: "🇮🇪", colors: ["#169B62", "#FFFFFF", "#FF883E"], label: { en: "Ireland", pt: "Irlanda" } },
  { code: "us", flag: "🇺🇸", colors: ["#B22234", "#FFFFFF", "#3C3B6E"], label: { en: "United States", pt: "Estados Unidos" } },
  { code: "ca", flag: "🇨🇦", colors: ["#FF0000", "#FFFFFF", "#FF0000"], label: { en: "Canada", pt: "Canadá" } },
];